import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Truck,
  Box,
  Barcode,
  Calculator,
  Info,
  RefreshCcw,
  Save,
  Trash2,
  FileText,
  Calendar,
  Hash,
  BadgeDollarSign,
  MapPin,
  Check,
  Package,
  TrendingUp,
  AlertCircle,
  Link as LinkIcon,
  Search,
  X,
  Replace,
  Boxes,
  Percent,
  CalendarClock,
  Plus
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  isVariantTracked,
  isVariantActive,
  sortVariantsByCurve,
  getVariantAttributesLabel,
  applyStockDelta,
} from '../utils/variantStock';

// Talles y colores: un renglón de un producto con talles necesita la variante
// que entra al stock. Con un solo talle activo se preselecciona.
const camposTalleCompra = (p) => {
  // Vencimientos: el renglón guarda la marca del producto; si aplica lo decide
  // productTracksExpiry con el lógico de la empresa. `lotes` se arma al editar.
  const venc = {
    controla_vencimiento: Boolean(p?.controla_vencimiento),
    vida_util_dias: p?.vida_util_dias ?? null,
    lotes: null,
  };
  if (!isVariantTracked(p)) return { stock_tracking: p?.stock_tracking || 'product', variantes: [], variant_id: null, ...venc };
  const variantes = sortVariantsByCurve((p.product_variants || []).filter(isVariantActive));
  return { stock_tracking: 'variant', variantes, variant_id: variantes.length === 1 ? variantes[0].id : null, ...venc };
};

import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import { empresaComputaCreditoFiscal } from '../utils/condicionFiscal';
import { round2 } from '../utils/afipIva';
import {
  getSubtotalItem,
  calcularItemsCompra,
  ivaTotalPorProducto,
  construirIvaBreakdown,
} from '../utils/comprasIva';
import { applyActiveProductsFilter } from '../utils/productStatus';
import { fetchUltimoCosto, describirOrigen } from '../utils/ultimoCosto';
import {
  addDaysYmd,
  moverStockConLotes,
  productTracksExpiry,
  toLotePayload,
  validateLotesEntrada,
} from '../utils/stockLotes';
import { formatDateOnlyValue } from '../utils/localDateTime';

// --- Vencimientos y lotes en la recepción -------------------------------------
const nuevoLoteUid = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Lotes efectivos del renglón. Con un solo lote su cantidad es siempre el total
// del renglón (unidades a stock); partido en varios, cada uno lleva la suya.
// Sin cargar nada, el vencimiento se sugiere con la vida útil del producto.
const lotesEfectivosRenglon = (item, fechaCompraYmd) => {
  const lista = Array.isArray(item?.lotes) && item.lotes.length > 0
    ? item.lotes
    : [{
      uid: 'unico',
      lote_codigo: '',
      fecha_vencimiento: item?.vida_util_dias ? addDaysYmd(fechaCompraYmd, item.vida_util_dias) : '',
    }];
  if (lista.length === 1) return [{ ...lista[0], cantidad: Number(item?._unidadesStock) || 0 }];
  return lista;
};

const LotesCompraEditor = ({ item, fechaCompraYmd, onChange }) => {
  const lotes = lotesEfectivosRenglon(item, fechaCompraYmd);
  const partido = lotes.length > 1;
  const total = Number(item?._unidadesStock) || 0;
  const suma = lotes.reduce((acc, l) => acc + (Number(l.cantidad) || 0), 0);
  const descuadre = partido && Math.abs(suma - total) > 1e-6;

  const editar = (uid, patch) => onChange(lotes.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  const dividir = () => onChange([
    ...lotes.map((l) => ({ ...l, uid: l.uid === 'unico' ? nuevoLoteUid() : l.uid })),
    { uid: nuevoLoteUid(), lote_codigo: '', fecha_vencimiento: '', cantidad: '' },
  ]);
  const quitar = (uid) => onChange(lotes.filter((l) => l.uid !== uid));

  return (
    <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/60 p-2">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-violet-700">
          <CalendarClock size={12} /> Vencimiento {partido ? `· ${lotes.length} lotes` : ''}
        </div>
        <button
          type="button"
          onClick={dividir}
          className="text-[9px] font-black uppercase text-violet-700 hover:text-violet-900 flex items-center gap-1"
          title="Recibir este producto con más de un vencimiento"
        >
          <Plus size={11} /> Dividir
        </button>
      </div>
      <div className="flex flex-col gap-1.5">
        {lotes.map((l) => {
          const vencido = l.fecha_vencimiento && l.fecha_vencimiento < fechaCompraYmd;
          return (
            <div key={l.uid} className="flex flex-wrap items-center gap-1.5">
              <input
                type="date"
                value={l.fecha_vencimiento || ''}
                onChange={(e) => editar(l.uid, { fecha_vencimiento: e.target.value })}
                className={`h-8 px-2 rounded-lg border text-[11px] font-bold bg-white ${
                  !l.fecha_vencimiento ? 'border-amber-300' : vencido ? 'border-rose-300 text-rose-600' : 'border-slate-200 text-slate-700'
                }`}
                title={vencido ? 'Ya está vencido a la fecha de la compra' : 'Fecha de vencimiento'}
              />
              <input
                type="text"
                value={l.lote_codigo || ''}
                onChange={(e) => editar(l.uid, { lote_codigo: e.target.value })}
                placeholder="Lote (opcional)"
                className="h-8 w-28 px-2 rounded-lg border border-slate-200 bg-white text-[11px] font-bold uppercase text-slate-700"
              />
              {partido && (
                <>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={l.cantidad ?? ''}
                    onChange={(e) => editar(l.uid, { cantidad: e.target.value })}
                    placeholder="Unid."
                    className="h-8 w-20 px-2 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-right text-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() => quitar(l.uid)}
                    className="p-1 text-slate-300 hover:text-rose-500"
                    title="Quitar lote"
                  >
                    <X size={14} />
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {partido && (
        <div className={`mt-1.5 text-[9px] font-bold ${descuadre ? 'text-rose-600' : 'text-slate-500'}`}>
          Lotes: {suma.toLocaleString('es-AR')} de {total.toLocaleString('es-AR')} unidades a stock
          {descuadre ? ' — tienen que sumar lo mismo' : ''}
        </div>
      )}
    </div>
  );
};

const PRODUCT_SEARCH_LIMIT = 50;
const MANUAL_LINK_SEARCH_LIMIT = 30;

// ============================================================
// DecimalInput: input numérico que acepta coma O punto como decimal
// ============================================================
// - type="text" + inputMode="decimal" para que aparezca el teclado numérico
//   en mobile y para evitar las limitaciones de <input type="number"> que
//   no acepta valores intermedios como "7996." o coma como separador.
// - Mantiene un display string interno para permitir tipear naturalmente.
// - Emite SIEMPRE un número al padre vía onChange(num).
// - Se sincroniza con `value` cuando el input no está enfocado.
const DecimalInput = ({ value, onChange, className = '', placeholder = '', allowZeroDisplay = false, ...rest }) => {
  const numToStr = (n) => {
    if (n === null || n === undefined || n === '') return '';
    const num = Number(n);
    if (Number.isNaN(num)) return '';
    // Si value es 0 y no se pidió mostrarlo, devolver vacío para que aparezca el placeholder
    if (num === 0 && !allowZeroDisplay) return '';
    return String(num);
  };
  const [display, setDisplay] = useState(() => numToStr(value));
  const [focused, setFocused] = useState(false);

  // Sincronizar display con value cuando el input NO está enfocado
  useEffect(() => {
    if (!focused) {
      setDisplay(numToStr(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    let raw = e.target.value;
    // Convertir coma a punto
    raw = raw.replace(',', '.');
    // Permitir: vacío, signo opcional, dígitos, un único punto, dígitos
    // Tolera estados parciales como "", "-", "7.", ".5", "7"
    if (raw !== '' && !/^-?[0-9]*\.?[0-9]*$/.test(raw)) {
      return; // ignorar tecla inválida
    }
    setDisplay(raw);
    // Emitir número al padre (solo si parsea)
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
      onChange(0);
    } else {
      const num = parseFloat(raw);
      onChange(Number.isNaN(num) ? 0 : num);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    // Al perder foco, normalizar: si el display quedó en un estado parcial,
    // reescribirlo desde el value numérico definitivo
    if (display === '' || display === '-' || display === '.' || display === '-.') {
      setDisplay(numToStr(value));
    } else {
      // Asegurar que el display refleje exactamente lo que el padre tiene
      const parsed = parseFloat(display.replace(',', '.'));
      if (!Number.isNaN(parsed)) {
        setDisplay(numToStr(parsed));
      }
    }
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={className}
      placeholder={placeholder}
      value={display}
      onChange={handleChange}
      onFocus={(e) => {
        setFocused(true);
        // Selecciona lo que ya hay cargado: sin esto habia que hacer triple
        // clic para reemplazar la cantidad en vez de concatenar digitos.
        e.target.select();
      }}
      onBlur={handleBlur}
    />
  );
};

/**
 * ComprasNueva - Terminal de Abastecimiento
 * - Incluye lógica de MATCHING AUTOMÁTICO por nombre de producto
 * - Validación detallada de campos obligatorios
 * - Vinculación manual de productos no encontrados
 * - El total se calcula siempre por la sumatoria de productos
 * - FIX DEFINITIVO: Actualiza precios de venta directos y recalcula toda la cadena de recetas.
 */
const ComprasNueva = ({ onNavigate }) => {
  const { profile, organization } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showSuccessCheck, setShowSuccessCheck] = useState(false);

  // Condición fiscal de la empresa. Solo si es Responsable Inscripto (RI) el IVA
  // de compra se trata como CRÉDITO FISCAL: la alícuota se toma de cada producto,
  // se arma el desglose para el Libro IVA y el IVA NO se carga al costo.
  // Para Monotributo / Exento / cualquier otro caso (o si no se puede determinar)
  // se mantiene el comportamiento histórico: IVA global a nivel factura, al costo.
  const [empresaEsRI, setEmpresaEsRI] = useState(false);

  // Maestros
  const [sitios, setSitios] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [condicionesPago, setCondicionesPago] = useState([]);

  // Condición de pago seleccionada (objeto completo)
  const [selectedCondicion, setSelectedCondicion] = useState(null);

  // Proveedores (selector con búsqueda)
  const [busquedaProv, setBusquedaProv] = useState('');
  const [showProvList, setShowProvList] = useState(false);
  const [selectedProv, setSelectedProv] = useState(null);

  // Sitio destino
  const [selectedSitio, setSelectedSitio] = useState('');

  // Cuenta de origen del pago para compras Contado (banco/billetera o caja en efectivo)
  const [bancos, setBancos] = useState([]);
  const [turnoCajaAbierto, setTurnoCajaAbierto] = useState(null);
  const [cuentaOrigen, setCuentaOrigen] = useState(''); // '' | 'caja' | <bankId>

  // Clave de idempotencia: se genera una sola vez por intento de guardado y se
  // reusa si el handler se dispara dos veces (doble-click / retry), para que el
  // RPC devuelva la misma compra en lugar de duplicarla. Se limpia al confirmar.
  const idempotencyKeyRef = useRef(null);

  // Productos (Buscador General)
  const [busquedaProd, setBusquedaProd] = useState('');
  const [productosBusqueda, setProductosBusqueda] = useState([]);
  const [items, setItems] = useState([]);

  // --- ESTADOS PARA VINCULACIÓN MANUAL (Por Renglón) ---
  const [editingRowId, setEditingRowId] = useState(null); 
  const [rowSearchTerm, setRowSearchTerm] = useState(''); 
  const [rowSearchResults, setRowSearchResults] = useState([]); 

  // Datos comprobante (cabecera)
  const [tipoComprobante, setTipoComprobante] = useState('Factura');
  const [numeroComprobante, setNumeroComprobante] = useState('');
  
  const fmtLocalYMD = (d = new Date()) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const fmtLocalHM = (d = new Date()) => {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const [uiMessage, setUiMessage] = useState(null);

  const [fechaComprobante, setFechaComprobante] = useState(() => fmtLocalYMD(new Date()));
  const [horaComprobante, setHoraComprobante] = useState(() => fmtLocalHM(new Date()));
  const [condicionPago, setCondicionPago] = useState('Cuenta corriente'); // Fallback label
  const [observaciones, setObservaciones] = useState('');

  // Totales: automático vs manual
  const [modoTotalManual, setModoTotalManual] = useState(false);
  const [subtotalManual, setSubtotalManual] = useState(0);
  const [ivaManual, setIvaManual] = useState(0);
  const [totalManual, setTotalManual] = useState(0);

  // --- IVA total a prorratear entre los productos (modo Auto) ---
  // Permite cargar el IVA de la factura al final y que se distribuya proporcionalmente
  // entre los costos unitarios de cada producto según el peso de su subtotal.
  //
  // El IVA se puede ingresar de dos formas:
  //  - 'porcentaje' (default 21%): el monto se calcula sobre el subtotal de renglones
  //  - 'monto':      el usuario tipea el monto exacto en pesos (útil cuando la factura
  //                  trae un IVA con redondeos o cuando es factura A con IVA discriminado)
  const [ivaInputModo, setIvaInputModo] = useState('porcentaje'); // 'porcentaje' | 'monto'
  const [ivaPorcentaje, setIvaPorcentaje] = useState(21);
  const [ivaMontoManual, setIvaMontoManual] = useState(0);
  // Si está en true: el IVA se prorratea al costo unitario de cada producto.
  // Si está en false: el IVA queda solo en la cabecera de la factura, sin afectar costos.
  const [aplicarIvaACostos, setAplicarIvaACostos] = useState(true);
  // Toggle maestro de IVA:
  //  - true  (Activo):   se calcula el IVA según el modo (% o $) configurado por el usuario.
  //  - false (Inactivo): el IVA queda fijo en 0, sin importar el modo ni el %/monto cargado.
  // Por defecto arranca INACTIVO para que al cargar la página no se aplique IVA automáticamente
  // a la factura. El usuario debe activarlo manualmente si la factura lleva IVA.
  const [ivaActivo, setIvaActivo] = useState(false);

  const inputProdRef = useRef(null);
  const provContainerRef = useRef(null);
  const rowInputRef = useRef(null); 

  const fetchData = useCallback(async () => {
    if (!profile?.organization_id) return;

    try {
      const [resSitios, resProvs, resConds, resBancos, resTurno, resBilling] = await Promise.all([
        supabase
          .from('sitios')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .order('nombre', { ascending: true }),
        supabase
          .from('proveedores')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .order('razon_social', { ascending: true }),
        supabase
          .from('payment_conditions')
          .select('id, name, days_until_due')
          .eq('organization_id', profile.organization_id)
          .eq('is_active', true)
          .order('days_until_due', { ascending: true }),
        supabase
          .from('banks')
          .select('id, name, current_balance, is_active')
          .eq('organization_id', profile.organization_id)
          .eq('is_active', true)
          .order('name', { ascending: true }),
        supabase
          .from('turnos_caja')
          .select('id, sitio_id')
          .eq('organization_id', profile.organization_id)
          .eq('estado', 'Abierto')
          .maybeSingle(),
        supabase
          .from('billing_entities')
          .select('fiscal_configuration, tax_condition')
          .eq('organization_id', profile.organization_id)
      ]);

      if (resSitios.error) throw resSitios.error;
      if (resProvs.error) throw resProvs.error;

      // Determinar si la empresa es Responsable Inscripto. Tomamos la primera
      // entidad de facturación (CUIT) y caemos a la condición de la organización.
      // Si ningún dato indica claramente RI, NO se activa el nuevo tratamiento:
      // los Monotributistas (y casos ambiguos) conservan el flujo histórico.
      const billingEntity = (resBilling?.data || [])[0] || null;
      setEmpresaEsRI(empresaComputaCreditoFiscal({ billingEntity, organization }));

      const availableSites = resSitios.data || [];
      setSitios(availableSites);
      setProveedores(resProvs.data || []);
      setBancos(resBancos.data || []);
      setTurnoCajaAbierto(resTurno.data || null);

      const conds = resConds.data || [];
      setCondicionesPago(conds);
      // Seleccionar la primera condición por defecto (si hay)
      if (conds.length > 0 && !selectedCondicion) {
        setSelectedCondicion(conds[0]);
        setCondicionPago(conds[0].name);
      }

      setSelectedSitio((prev) =>
        resolvePreferredSiteId(availableSites, {
          currentSiteId: prev,
        }),
      );
    } catch (err) {
      console.error(err);
      setUiMessage({ type: 'error', text: 'Error al cargar maestros' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, organization?.tax_condition, organization?.fiscal_configuration]);

  // Carga inicial de datos
  useEffect(() => {
    if (profile?.organization_id) fetchData();

    const handleClickOutside = (e) => {
      if (provContainerRef.current && !provContainerRef.current.contains(e.target)) {
        setShowProvList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profile?.organization_id, fetchData]);

  // Focus automático cuando se abre el modo edición de un renglón
  useEffect(() => {
    if (editingRowId && rowInputRef.current) {
        rowInputRef.current.focus();
    }
  }, [editingRowId]);

  useEffect(() => {
    if (selectedSitio) persistSelectedSiteId(selectedSitio);
  }, [selectedSitio]);

  // --- LOGICA INTELIGENTE DE RECUPERACIÓN Y MATCHING OCR ---
  useEffect(() => {
    const processOcrData = async () => {
        if (proveedores.length > 0) {
            const storedData = localStorage.getItem('temp_ocr_data');
            
            if (storedData) {
                try {
                    const ocrData = JSON.parse(storedData);
                    console.log("Datos OCR recuperados:", ocrData);

                    // 1. Matchear Proveedor
                    if (ocrData.proveedor_nombre) {
                        const foundProv = proveedores.find(p => 
                            p.razon_social.toLowerCase().includes(ocrData.proveedor_nombre.toLowerCase())
                        );
                        if (foundProv) {
                            setSelectedProv(foundProv);
                            toast.success(`Proveedor vinculado: ${foundProv.razon_social}`);
                        } else {
                            toast("Proveedor no encontrado automáticamente.", { icon: '⚠️' });
                        }
                    }

                    // 2. Fechas
                    if (ocrData.fecha) setFechaComprobante(ocrData.fecha);
                    if (ocrData.hora) setHoraComprobante(ocrData.hora);

                    // 3. Items + MATCHING DE PRODUCTOS
                    if (ocrData.items_detectados?.length > 0) {
                        const { data: allProducts } = await applyActiveProductsFilter(
                          supabase
                          .from('products')
                          .select('id, name, barcode, cost_price, unit_of_measure, units_per_package, iva_percentage, stock_tracking, controla_vencimiento, vida_util_dias, product_variants(id, talle, talle_orden, color_id, activo, archivado_at, sku, attributes, colores(nombre))')
                          .eq('organization_id', profile.organization_id)
                        );

                        let itemsVinculadosCount = 0;

                        const mappedItems = ocrData.items_detectados.map(i => {
                            const rawName = (i.nombre_detectado || "").toLowerCase().trim();
                            let foundProduct = null;

                            if (allProducts) {
                                foundProduct = allProducts.find(p => {
                                    const dbName = p.name.toLowerCase().trim();
                                    return dbName === rawName || rawName.includes(dbName) || dbName.includes(rawName);
                                });
                            }

                            if (foundProduct) itemsVinculadosCount++;

                            const uxp = Number(foundProduct?.units_per_package) || Number(i.unidades_por_paquete) || 1;

                            return {
                                id: crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
                                product_id: foundProduct ? foundProduct.id : null,
                                ...camposTalleCompra(foundProduct),
                                nombre: foundProduct ? foundProduct.name : i.nombre_detectado,
                                barcode: foundProduct ? foundProduct.barcode : '',
                                uom: foundProduct ? foundProduct.unit_of_measure : 'un',
                                cantidad: Number(i.cantidad) || 1,
                                costo: Number(i.costo_unitario) || 0,
                                // Alícuota: SIEMPRE del maestro del producto. Si el renglón
                                // OCR aún no está vinculado, default 21% hasta que se vincule.
                                iva_percentage: foundProduct?.iva_percentage != null ? Number(foundProduct.iva_percentage) : 21,
                                es_nuevo: true,
                                match_type: foundProduct ? 'auto' : null,
                                por_paquete: uxp > 1,
                                unidades_por_paquete: uxp
                            };
                        });

                        setItems(mappedItems);

                        if (itemsVinculadosCount > 0) {
                            toast.success(`${itemsVinculadosCount} productos vinculados automáticamente`, {
                                icon: '🔗',
                                duration: 4000
                            });
                        }
                    }

                    // 4. Totales
                    if (ocrData.total_detectado) {
                        setModoTotalManual(false);
                        setTotalManual(ocrData.total_detectado);
                        setSubtotalManual(ocrData.total_detectado / 1.21);
                        setIvaManual(ocrData.total_detectado - (ocrData.total_detectado / 1.21));
                    }

                    // 4b. IVA detectado por el OCR a prorratear (lo cargamos en modo monto exacto)
                    //     Si el OCR detectó IVA, activamos automáticamente el toggle maestro.
                    if (ocrData.iva_detectado) {
                        setIvaActivo(true);
                        setIvaInputModo('monto');
                        setIvaMontoManual(Number(ocrData.iva_detectado) || 0);
                    }

                    localStorage.removeItem('temp_ocr_data');

                } catch (e) {
                    console.error("Error procesando OCR data", e);
                }
            }
        }
    };

    processOcrData();
  }, [proveedores, profile?.organization_id]);

  // Último costo de cada producto de la compra. El costo precargado es el último
  // del maestro, no el de la factura: se rotula con su fecha y origen para que no
  // se confunda. Se pide una sola vez por producto mientras dure la carga.
  const [ultimosCostos, setUltimosCostos] = useState({});
  const ultimosCostosPedidosRef = useRef(new Set());
  useEffect(() => {
    const orgId = profile?.organization_id;
    if (!orgId) return;
    if (items.length === 0) {
      // Compra nueva (o recién guardada): los costos pudieron cambiar, se vuelven a pedir.
      if (ultimosCostosPedidosRef.current.size) {
        ultimosCostosPedidosRef.current.clear();
        setUltimosCostos({});
      }
      return;
    }
    const pendientes = [...new Set(items.map((i) => i.product_id).filter(Boolean))]
      .filter((pid) => !ultimosCostosPedidosRef.current.has(pid));
    pendientes.forEach((pid) => {
      ultimosCostosPedidosRef.current.add(pid);
      fetchUltimoCosto(supabase, { organizationId: orgId, productId: pid })
        .then((info) => setUltimosCostos((prev) => ({ ...prev, [pid]: info })))
        .catch(() => setUltimosCostos((prev) => ({ ...prev, [pid]: null })));
    });
  }, [items, profile?.organization_id]);

  const currency = (n) =>
    (Number(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ============================================================
  // HELPERS DE CÁLCULO POR PAQUETE Y PRORRATEO DE IVA
  // ============================================================
  // Cuando un item está en modo "por paquete":
  //   - item.cantidad        = cantidad de PAQUETES comprados
  //   - item.costo           = costo del PAQUETE (lo que aparece en la factura)
  //   - item.unidades_por_paquete = unidades sueltas que trae cada paquete
  // Para stock y costo unitario real:
  //   - unidades_totales = cantidad × unidades_por_paquete
  //   - costo_unitario   = costo / unidades_por_paquete
  //
  // Cuando NO está en modo paquete:
  //   - item.cantidad = unidades sueltas
  //   - item.costo    = costo unitario directo
  //   - unidades_por_paquete = 1
  // ============================================================
  const subtotalRenglones = useMemo(
    () => items.reduce((acc, i) => acc + getSubtotalItem(i), 0),
    [items]
  );

  // Monto de IVA calculado según el modo de input elegido por el usuario.
  // - Si ivaActivo === false: siempre 0 (el toggle maestro está apagado).
  // - 'porcentaje': subtotal × % / 100   (ej: 21% sobre $745.245 → $156.501)
  // - 'monto':      monto exacto tipeado (ej: $156.501,62 de la factura)
  const ivaMontoCalculado = useMemo(() => {
    if (!ivaActivo) return 0;
    if (ivaInputModo === 'porcentaje') {
      const pct = Number(ivaPorcentaje) || 0;
      return subtotalRenglones * (pct / 100);
    }
    return Number(ivaMontoManual) || 0;
  }, [ivaActivo, ivaInputModo, ivaPorcentaje, ivaMontoManual, subtotalRenglones]);

  // ¿Usamos IVA por producto (crédito fiscal)? Solo para Responsable Inscripto.
  // Para Monotributo / Exento / casos ambiguos sigue el flujo histórico intacto.
  const usarIvaPorProducto = empresaEsRI;

  // IVA a prorratear: solo si el usuario decidió aplicarlo a costos (flujo histórico).
  // En el flujo RI no se prorratea al costo: el IVA es crédito fiscal.
  const ivaProrrateableTotal = useMemo(
    () => (usarIvaPorProducto ? 0 : (aplicarIvaACostos ? ivaMontoCalculado : 0)),
    [usarIvaPorProducto, aplicarIvaACostos, ivaMontoCalculado]
  );

  // Renglones calculados (IVA, unidades a stock, costos). La lógica vive en
  // utils/comprasIva.js para poder testearla sin renderizar React.
  const itemsCalculados = useMemo(
    () => calcularItemsCompra({ items, esRI: usarIvaPorProducto, ivaProrrateableTotal }),
    [items, ivaProrrateableTotal, usarIvaPorProducto]
  );

  // IVA total cuando se calcula por producto (RI): suma de IVA de cada renglón.
  const ivaPorProductoTotal = useMemo(
    () => (usarIvaPorProducto ? ivaTotalPorProducto(itemsCalculados) : 0),
    [usarIvaPorProducto, itemsCalculados]
  );

  // Desglose por alícuota para el Libro IVA Compras (un grupo por tasa; 0% = exento).
  const ivaBreakdown = useMemo(
    () => (usarIvaPorProducto ? construirIvaBreakdown(itemsCalculados) : []),
    [usarIvaPorProducto, itemsCalculados]
  );

  const unidadesTotalesCompra = useMemo(
    () => itemsCalculados.reduce((acc, i) => acc + (i._unidadesStock || 0), 0),
    [itemsCalculados]
  );

  // ============================================================
  // TOTALES DE LA COMPRA
  // ============================================================
  // Modo Auto:  subtotal = suma renglones | IVA = ivaMontoCalculado (% o $) | total = subtotal + IVA
  // Modo Manual: subtotal, IVA y total tipeados a mano (caso edge)
  // ============================================================
  const totalItemsAuto = subtotalRenglones;

  const subtotalCompra = useMemo(
    () => (usarIvaPorProducto ? subtotalRenglones : (modoTotalManual ? Number(subtotalManual) || 0 : subtotalRenglones)),
    [usarIvaPorProducto, modoTotalManual, subtotalManual, subtotalRenglones]
  );
  const ivaCompra = useMemo(
    () => (usarIvaPorProducto ? ivaPorProductoTotal : (modoTotalManual ? Number(ivaManual) || 0 : ivaMontoCalculado)),
    [usarIvaPorProducto, ivaPorProductoTotal, modoTotalManual, ivaManual, ivaMontoCalculado]
  );
  const totalCompra = useMemo(
    () => (usarIvaPorProducto
      ? round2(subtotalRenglones + ivaPorProductoTotal)
      : (modoTotalManual ? Number(totalManual) || 0 : subtotalCompra + ivaCompra)),
    [usarIvaPorProducto, subtotalRenglones, ivaPorProductoTotal, modoTotalManual, totalManual, subtotalCompra, ivaCompra]
  );

  const handleProdInput = async (value) => {
    setBusquedaProd(value);

    const q = (value || '').trim();
    if (!q || !profile?.organization_id) {
      setProductosBusqueda([]);
      return;
    }

    const { data, error } = await applyActiveProductsFilter(
      supabase
      .from('products')
      .select('id, name, barcode, cost_price, unit_of_measure, brand, units_per_package, iva_percentage, units_of_measure(abbreviation), stock_tracking, controla_vencimiento, vida_util_dias, product_variants(id, talle, talle_orden, color_id, activo, archivado_at, sku, attributes, colores(nombre))')
      .eq('organization_id', profile.organization_id)
    )
      .or(`name.ilike.%${q}%,barcode.ilike.%${q}%`)
      .limit(PRODUCT_SEARCH_LIMIT);

    if (error) {
      console.error(error);
      return;
    }
    setProductosBusqueda(data || []);
  };

  const addItem = (p) => {
    const uid = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const uom = p?.units_of_measure?.abbreviation || p?.unit_of_measure || 'un';
    const uxp = Number(p?.units_per_package) || 1;

    setItems((prev) => [
      ...prev,
      {
        id: uid,
        product_id: p.id,
        nombre: p.name,
        barcode: p.barcode || '',
        uom,
        cantidad: 1,
        costo: Number(p.cost_price) || 0,
        marca: p.brand || '',
        // Alícuota IVA: SIEMPRE se decide en el producto (read-only en la compra).
        iva_percentage: p?.iva_percentage != null ? Number(p.iva_percentage) : 21,
        // --- Modo paquete: por defecto desactivado, pero precarga el dato del maestro ---
        por_paquete: uxp > 1,
        unidades_por_paquete: uxp,
        ...camposTalleCompra(p)
      }
    ]);

    setBusquedaProd('');
    setProductosBusqueda([]);
    setTimeout(() => inputProdRef.current?.focus(), 80);
  };
  
  const startEditingRow = (item) => {
      setEditingRowId(item.id);
      setRowSearchTerm(''); 
      setRowSearchResults([]);
  };

  const cancelEditingRow = () => {
      setEditingRowId(null);
      setRowSearchTerm('');
      setRowSearchResults([]);
  };

  const handleRowSearchInput = async (value) => {
      setRowSearchTerm(value);
      const q = (value || '').trim();
      if (!q || !profile?.organization_id) {
          setRowSearchResults([]);
          return;
      }

      const { data } = await applyActiveProductsFilter(
        supabase
        .from('products')
        .select('id, name, barcode, cost_price, unit_of_measure, brand, units_per_package, iva_percentage, units_of_measure(abbreviation), stock_tracking, controla_vencimiento, vida_util_dias, product_variants(id, talle, talle_orden, color_id, activo, archivado_at, sku, attributes, colores(nombre))')
        .eq('organization_id', profile.organization_id)
      )
        .or(`name.ilike.%${q}%,barcode.ilike.%${q}%`)
        .limit(MANUAL_LINK_SEARCH_LIMIT);

      setRowSearchResults(data || []);
  };

  const confirmRowLink = (itemOriginal, productoSeleccionado) => {
      const uom = productoSeleccionado?.units_of_measure?.abbreviation || productoSeleccionado?.unit_of_measure || 'un';
      const uxpMaestro = Number(productoSeleccionado?.units_per_package) || 1;

      const newItems = items.map(i => {
          if (i.id === itemOriginal.id) {
              // Si el item original NO tenía unidades por paquete personalizadas,
              // tomamos las del maestro recién vinculado
              const usaUxpMaestro = !i.unidades_por_paquete || i.unidades_por_paquete <= 1;
              return {
                  ...i,
                  product_id: productoSeleccionado.id,
                  nombre: productoSeleccionado.name,
                  barcode: productoSeleccionado.barcode || '',
                  uom: uom,
                  marca: productoSeleccionado.brand || '',
                  // Al vincular, la alícuota pasa a ser la del producto del maestro.
                  iva_percentage: productoSeleccionado?.iva_percentage != null
                    ? Number(productoSeleccionado.iva_percentage)
                    : (i.iva_percentage ?? 21),
                  unidades_por_paquete: usaUxpMaestro ? uxpMaestro : i.unidades_por_paquete,
                  por_paquete: usaUxpMaestro ? (uxpMaestro > 1) : i.por_paquete,
                  ...camposTalleCompra(productoSeleccionado),
              };
          }
          return i;
      });

      setItems(newItems);
      cancelEditingRow();
      toast.success("Producto vinculado correctamente");
  };

  const updateItem = (itemId, patch) => {
    setItems((prev) => prev.map((x) => (x.id === itemId ? { ...x, ...patch } : x)));
  };

  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((x) => x.id !== itemId));
  };

  const selectProveedor = (prov) => {
    setSelectedProv(prov);
    setShowProvList(false);
    setBusquedaProv('');
  };

  const resetProveedor = () => {
    setSelectedProv(null);
    setBusquedaProv('');
    setShowProvList(false);
  };

  const handleGuardar = async () => {
    if (!profile?.organization_id) {
      toast.error('Error Crítico: No se identificó la organización. Intenta recargar la página.');
      return;
    }

    const faltantes = [];

    if (!selectedProv?.id) faltantes.push("• Proveedor");
    if (!selectedSitio) faltantes.push("• Sitio de destino");
    if (!tipoComprobante) faltantes.push("• Tipo de comprobante");
    if (!fechaComprobante) faltantes.push("• Fecha");
    if (!Array.isArray(items) || items.length === 0) faltantes.push("• Al menos un producto/renglón");

    if (faltantes.length > 0) {
        toast((t) => (
            <div className="flex flex-col gap-2">
                <span className="font-bold text-rose-600">Faltan datos obligatorios:</span>
                <div className="text-xs text-slate-600 flex flex-col gap-1">
                    {faltantes.map((f, i) => <span key={i}>{f}</span>)}
                </div>
                <button onClick={() => toast.dismiss(t.id)} className="text-[10px] underline mt-1 text-slate-400 self-end">
                    Entendido
                </button>
            </div>
        ), { duration: 5000, icon: '⚠️' });
        return;
    }

    const itemsSinTalle = items.filter((i) => i.product_id && i.stock_tracking === 'variant' && !i.variant_id);
    if (itemsSinTalle.length > 0) {
        toast.error(`Elegí el talle/color de: ${itemsSinTalle.map((i) => i.nombre).join(', ')}.`);
        return;
    }

    // Vencimientos: cada renglón que controla vencimiento necesita fecha y,
    // partido en lotes, que las cantidades sumen las unidades a stock.
    const lotesVencidos = [];
    for (const it of itemsCalculados) {
        if (!it.product_id || !productTracksExpiry(organization, it)) continue;
        const v = validateLotesEntrada(lotesEfectivosRenglon(it, fechaComprobante), it._unidadesStock);
        if (!v.ok) {
            toast.error(`${it.nombre}: ${v.error}`);
            return;
        }
        v.vencidos.forEach((l) => lotesVencidos.push(`${it.nombre} (${formatDateOnlyValue(l.fecha_vencimiento)})`));
    }
    if (lotesVencidos.length > 0
        && !window.confirm(`Estos productos ya están vencidos:\n\n${lotesVencidos.join('\n')}\n\n¿Registrar la compra igual?`)) {
        return;
    }

    const itemsSinID = items.filter(i => !i.product_id);
    if (itemsSinID.length > 0) {
        toast((t) => (
            <div className="flex flex-col gap-2">
                <span className="font-bold text-amber-600">Atención: Productos sin vincular</span>
                <span className="text-xs text-slate-600">
                    Tienes <b>{itemsSinID.length} ítem(s)</b> que no están asociados a productos de tu sistema.
                    Debes seleccionarlos (botón Vincular) o eliminarlos antes de guardar.
                </span>
                <button onClick={() => toast.dismiss(t.id)} className="text-[10px] underline mt-1 text-slate-400 self-end">
                    Revisar
                </button>
            </div>
        ), { duration: 6000, icon: '🛑' });
        return;
    }

    // La condición de pago define el tratamiento financiero (contado vs. a plazo).
    // Sin condición elegida no podemos saber si la compra se paga ya o queda pendiente,
    // así que la exigimos en vez de asumir contado por defecto.
    if (!selectedCondicion) {
        toast.error('Elegí una condición de pago.');
        return;
    }

    const esContado = Number(selectedCondicion.days_until_due ?? 0) === 0;

    // Compra Contado: exigir la cuenta de origen del pago (banco o caja).
    if (esContado && !cuentaOrigen) {
        toast.error('Elegí la cuenta de origen del pago (Contado).');
        return;
    }

    setLoading(true);
    setUiMessage(null);

    try {
      const orgId = profile.organization_id;
      const fechaHora = new Date(`${fechaComprobante}T${horaComprobante}:00`);

      // 1. Guardar Compra
      // Guardamos en items el detalle COMPLETO para auditoría y reimpresión exacta:
      //  - quantity: unidades TOTALES (cantidad × unidades_por_paquete) → es lo que entra a stock
      //  - unit_cost: costo unitario FINAL (con IVA prorrateado si corresponde)
      //  - packages_qty, units_per_package, package_cost: detalle de paquete
      //  - subtotal_neto, iva_prorrateado, costo_unitario_neto: desglose contable
      const itemsPayload = itemsCalculados.map((i) => ({
        product_id: i.product_id,
        product_name: i.nombre,
        barcode: i.barcode || null,
        uom: i.uom || 'un',
        // Compatibilidad: quantity y unit_cost reflejan unidades reales y costo unitario final
        quantity: Number(i._unidadesStock) || 0,
        unit_cost: Number(i._costoUnitarioFinal) || 0,
        subtotal: (Number(i._subtotalNeto) || 0) + (Number(i._ivaProrrateado) || 0),
        // Detalle por paquete
        por_paquete: !!i.por_paquete,
        packages_qty: i.por_paquete ? (Number(i.cantidad) || 0) : null,
        units_per_package: i.por_paquete ? (Number(i.unidades_por_paquete) || 1) : 1,
        package_cost: i.por_paquete ? (Number(i.costo) || 0) : null,
        // Desglose contable
        subtotal_neto: Number(i._subtotalNeto) || 0,
        iva_prorrateado: Number(i._ivaProrrateado) || 0,
        costo_unitario_neto: Number(i._costoUnitarioNeto) || 0,
        costo_unitario_final: Number(i._costoUnitarioFinal) || 0,
        // Alícuota IVA del producto (snapshot) e importe de IVA del renglón.
        iva_percentage: Number(i.iva_percentage) || 0,
        iva_amount: Number(i._ivaProrrateado) || 0
      }));

      // 1 + 2 + (4-contado). NÚCLEO LEDGER ATÓMICO.
      // El RPC `registrar_compra` corre en una sola transacción server-side:
      //   - inserta la compra,
      //   - genera la deuda en la cuenta corriente del proveedor,
      //   - si es CONTADO (dias=0): emite el recibo formal (compra tipo 'Pago'),
      //     descuenta de banco/caja, cancela la deuda y deja la compra 'Pagada'.
      // Si cualquier paso falla, la base revierte TODO (sin estados a medias).
      // El stock/costos (paso 3), la proyección a plazo y el motor de recetas
      // (paso 5) siguen siendo client-side más abajo.
      const diasCondicion = Number(selectedCondicion?.days_until_due ?? 0);
      // Reusar la clave si ya hubo un intento (doble-click/retry); si no, generar una.
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      const { data: rpcData, error: rpcError } = await supabase.rpc('registrar_compra', {
        p: {
          organization_id: orgId,
          proveedor_id: selectedProv.id,
          proveedor_nombre: selectedProv.razon_social,
          fecha: fechaHora.toISOString(),
          tipo_comprobante: tipoComprobante,
          numero_comprobante: numeroComprobante || null,
          subtotal: subtotalCompra,
          iva: ivaCompra,
          total_general: totalCompra,
          // Desglose por alícuota para el Libro IVA Compras (solo RI; null en el resto).
          iva_breakdown: usarIvaPorProducto ? ivaBreakdown : null,
          condicion_pago: selectedCondicion.name || condicionPago,
          observaciones,
          sitio_id: selectedSitio || '',
          items: itemsPayload,
          dias_condicion: diasCondicion,
          cuenta_origen: esContado ? cuentaOrigen : '',
          turno_id: turnoCajaAbierto?.id || '',
          user_id: profile?.id || '',
          idempotency_key: idempotencyKeyRef.current
        }
      });
      if (rpcError) throw rpcError;
      const compraIdRegistrada = rpcData?.compra_id || null;

      // -------------------------------------------------------------
      // 3. STOCK, COSTOS DIRECTOS Y PRECIOS DE VENTA INMEDIATOS
      // -------------------------------------------------------------
      // Para cada renglón:
      //   - Stock se incrementa en UNIDADES TOTALES (cantidad × unidades_por_paquete)
      //   - cost_price se actualiza con el COSTO UNITARIO FINAL (con IVA prorrateado si corresponde)
      //   - Si units_per_package > 1, se persiste en products para futuras compras
      const purchasedProductIds = itemsCalculados.map(i => i.product_id).filter(Boolean);

      // Traemos info vital de los productos comprados (Margen y si actualizan precio)
      const { data: purchasedProductsInfo } = await supabase
        .from('products')
        .select('id, target_margin, auto_update_price, units_per_package')
        .in('id', purchasedProductIds);

      const pInfoMap = {};
      (purchasedProductsInfo || []).forEach(p => pInfoMap[p.id] = p);

      for (const it of itemsCalculados) {
        const pid = it.product_id;

        // --- Movimiento de Stock ---
        if (productTracksExpiry(organization, it)) {
          // Vencimientos y lotes: entra por la base con sus lotes (stock + kardex
          // + lotes en una sola transacción).
          const unidades = Number(it._unidadesStock || 0);
          if (unidades > 0) {
            await moverStockConLotes({
              supabase,
              organizationId: orgId,
              productId: pid,
              siteId: selectedSitio,
              delta: unidades,
              tipo: 'INGRESO',
              motivo: 'COMPRA',
              lotes: lotesEfectivosRenglon(it, fechaComprobante).map((l) => toLotePayload({
                ...l,
                costo_unitario: Number(it._costoUnitarioFinal || 0),
              })),
              origen: 'compra',
              refTipo: 'compra',
              refId: compraIdRegistrada,
              compraId: compraIdRegistrada,
              allowNegative: true,
            });
          }
        } else if (it.stock_tracking === 'variant') {
          // Talles y colores: entra al talle elegido (stock_variantes + kardex en la base).
          await applyStockDelta({
            supabase,
            organizationId: orgId,
            productId: pid,
            variantId: it.variant_id,
            siteId: selectedSitio,
            delta: Number(it._unidadesStock || 0),
            tipo: 'INGRESO',
            motivo: 'COMPRA',
          });
        } else {
          // --- Movimiento de Stock ---
          const { data: stockRow, error: stockGetErr } = await supabase
            .from('stock_levels')
            .select('id, quantity')
            .eq('organization_id', orgId)
            .eq('product_id', pid)
            .eq('site_id', selectedSitio)
            .maybeSingle();
          if (stockGetErr) throw stockGetErr;

          const qtyPrev = Number(stockRow?.quantity || 0);
          const qtyAdd = Number(it._unidadesStock || 0); // ★ UNIDADES TOTALES, no paquetes
          const qtyNew = qtyPrev + qtyAdd;

          if (stockRow?.id) {
            await supabase
              .from('stock_levels')
              .update({ quantity: qtyNew, updated_at: new Date().toISOString() })
              .eq('id', stockRow.id);
          } else {
            await supabase.from('stock_levels').insert([
              {
                organization_id: orgId,
                product_id: pid,
                site_id: selectedSitio,
                quantity: qtyNew,
                updated_at: new Date().toISOString()
              }
            ]);
          }

          await supabase.from('movimientos_stock').insert([
            {
              organization_id: orgId,
              producto_id: pid,
              tipo: 'INGRESO',
              cantidad: qtyAdd,
              motivo: 'COMPRA',
              sitio_destino: selectedSitio,
              created_at: new Date().toISOString()
            }
          ]);
        }

        // --- Actualización de Costo y Precio del Insumo Directo ---
        // El costo nuevo es el UNITARIO FINAL: incluye IVA prorrateado si correspondió,
        // y ya está dividido por las unidades por paquete.
        const newCost = Number(it._costoUnitarioFinal || 0);
        const pInfo = pInfoMap[pid];

        const upProd = { cost_price: newCost };
        const upVar = { cost_price: newCost };

        // Si el item fue cargado en modo paquete, persistimos las unidades por paquete
        // en el maestro para precargar en futuras compras.
        const uxpItem = Number(it.unidades_por_paquete) || 1;
        if (it.por_paquete && uxpItem > 1 && uxpItem !== Number(pInfo?.units_per_package || 1)) {
          upProd.units_per_package = uxpItem;
        }

        // ¡CLAVE! Si la materia prima tiene "auto_update_price", le recalculamos el precio de venta en este momento.
        if (pInfo && pInfo.auto_update_price) {
            const margin = Number(pInfo.target_margin || 0);
            const newPrice = Number((newCost * (1 + (margin / 100))).toFixed(2));
            upProd.sell_price = newPrice;
            upVar.price = newPrice;
        }

        await supabase.from('products').update(upProd).eq('id', pid);
        await supabase.from('product_variants').update(upVar).eq('product_id', pid);
      }

      // 4. Finanzas a plazo: la compra contado (recibo + egreso + cancelación) ya quedó
      // resuelta atómicamente en el RPC.
      //
      // IMPORTANTE: acá ANTES se insertaba una proyección manual en `proyecciones`
      // por el vencimiento. Eso DUPLICABA el egreso en Flujo de Fondos, porque el
      // motor (utils/flujoFondos.js) YA proyecta toda compra pendiente por su fecha
      // de vencimiento leyendo directamente de `compras`. Peor: al pagar la compra,
      // la proyección manual quedaba viva para siempre (egreso proyectado fantasma).
      // La compra a plazo no necesita ningún registro extra: el motor la toma sola.

      // -------------------------------------------------------------
      // 5. MOTOR DE CASCADA Y RESOLUCIÓN DE RECETAS (A PRUEBA DE FALLOS)
      // -------------------------------------------------------------
      try {
          // Traemos TODAS las recetas y TODOS los productos (para tener el mapa completo)
          const { data: allRecetas } = await supabase.from('recetas').select('*').eq('organization_id', orgId);
          const { data: allProducts } = await supabase.from('products').select('id, cost_price, auto_update_price, target_margin').eq('organization_id', orgId);
          
          if (allRecetas && allProducts && allRecetas.length > 0) {
              
              // Armamos un mapa con los costos iniciales de la base de datos
              const costMap = {};
              allProducts.forEach(p => { costMap[p.id] = Number(p.cost_price || 0); });

              // Sobrescribimos el mapa con los costos UNITARIOS FINALES (con IVA prorrateado
              // y divididos por unidades por paquete) que vienen de esta compra.
              itemsCalculados.forEach(it => {
                  if (it.product_id) {
                      costMap[it.product_id] = Number(it._costoUnitarioFinal || 0);
                  }
              });

              // Clonamos recetas para procesar en memoria
              const recetasVivas = allRecetas.map(r => ({...r}));
              let hasChanges = true;
              let loops = 0;
              const maxLoops = 4; // Límite de seguridad para evitar loops infinitos (permite hasta 4 niveles de sub-recetas)
              
              // Bucle de resolución: Sigue iterando hasta que los números dejen de moverse
              while (hasChanges && loops < maxLoops) {
                  hasChanges = false;
                  loops++;

                  for (const rec of recetasVivas) {
                      const prodFinal = allProducts.find(p => p.id === rec.producto_id);
                      if (!prodFinal) continue;

                      let costoTotalMezcla = 0;
                      let jsonModificado = false;

                      // Actualizamos cada ingrediente de la receta con el precio del mapa en vivo
                      const nuevosIngredientes = (rec.ingredientes || []).map(ing => {
                          const costoFresco = costMap[ing.id] !== undefined ? costMap[ing.id] : Number(ing.costo_unitario || 0);
                          
                          // Si el JSON de la base de datos tenía un número viejo, lo marcamos para actualizar la foto
                          if (Math.abs(Number(ing.costo_unitario) - costoFresco) > 0.001) {
                              jsonModificado = true;
                          }
                          
                          costoTotalMezcla += (costoFresco * Number(ing.quantity || 0));
                          return { ...ing, costo_unitario: costoFresco };
                      });

                      const rend = Number(rec.rendimiento) || 1;
                      const nuevoCostoUnitario = rend > 0 ? (costoTotalMezcla / rend) : 0;
                      const nuevoCostoRedondeado = Number(nuevoCostoUnitario.toFixed(2));

                      if (jsonModificado) {
                          rec.ingredientes = nuevosIngredientes;
                          rec.needsDbUpdate = true; // Avisamos que hay que guardar el JSON en DB
                          hasChanges = true; // Si una receta cambia internamente, hay que dar otra vuelta
                      }

                      // Verificamos si el costo del producto resultante cambió respecto al mapa global
                      const costoActualEnMapa = costMap[rec.producto_id] !== undefined ? costMap[rec.producto_id] : Number(prodFinal.cost_price || 0);
                      
                      if (Math.abs(costoActualEnMapa - nuevoCostoRedondeado) > 0.001) {
                          costMap[rec.producto_id] = nuevoCostoRedondeado; // Actualizamos el mapa para que otras recetas lo usen
                          rec.needsProductUpdate = true; // Avisamos que hay que cambiar el precio del producto en DB
                          hasChanges = true;
                      }
                  }
              }

              // PERSISTENCIA FINAL: Impactamos todos los cambios descubiertos en la DB
              for (const rec of recetasVivas) {
                  // Actualizamos el JSON para que el panel de Recetas no diga "Pendiente"
                  if (rec.needsDbUpdate) {
                      await supabase.from('recetas').update({ ingredientes: rec.ingredientes }).eq('id', rec.id);
                  }

                  // Actualizamos Costos y Precios de los productos armados por receta
                  if (rec.needsProductUpdate) {
                      const prodFinal = allProducts.find(p => p.id === rec.producto_id);
                      const nuevoCosto = costMap[rec.producto_id];

                      let nuevoPrecioVenta = undefined;
                      // Si el producto final (Ej: Hamburguesa) tiene el auto update, recalculamos con su margen
                      if (prodFinal && prodFinal.auto_update_price) {
                          const margin = Number(prodFinal.target_margin || 0);
                          nuevoPrecioVenta = Number((nuevoCosto * (1 + (margin / 100))).toFixed(2));
                      }

                      const upPayload = { cost_price: nuevoCosto };
                      if (nuevoPrecioVenta !== undefined) upPayload.sell_price = nuevoPrecioVenta;
                      await supabase.from('products').update(upPayload).eq('id', rec.producto_id);

                      const varPayload = { cost_price: nuevoCosto };
                      if (nuevoPrecioVenta !== undefined) varPayload.price = nuevoPrecioVenta;
                      await supabase.from('product_variants').update(varPayload).eq('product_id', rec.producto_id);
                  }
              }
          }
      } catch (syncErr) {
          console.error("Error silencioso en Motor de Cascada:", syncErr);
      }
      // -------------------------------------------------------------

      const diasFinal = Number(selectedCondicion?.days_until_due ?? 0);
      const origenLabel = cuentaOrigen === 'caja'
        ? 'Caja (efectivo)'
        : (bancos.find((b) => b.id === cuentaOrigen)?.name || 'cuenta seleccionada');
      const msgFinanzas = diasFinal > 0
        ? `Egreso proyectado a ${diasFinal} días en Flujo de Fondos.`
        : `Pago contado descontado de ${origenLabel}.`;
      setUiMessage({ type: 'success', text: `Compra registrada. Stock, costos y recetas actualizados. ${msgFinanzas}` });
      setShowSuccessCheck(true);
      setTimeout(() => setShowSuccessCheck(false), 1700);

      // Compra confirmada: la próxima necesita una clave de idempotencia nueva.
      idempotencyKeyRef.current = null;

      setItems([]);
      setNumeroComprobante('');
      setObservaciones('');
      setCuentaOrigen('');
      setModoTotalManual(false);
      setSubtotalManual(0);
      setIvaManual(0);
      setTotalManual(0);
      setIvaInputModo('porcentaje');
      setIvaPorcentaje(21);
      setIvaMontoManual(0);
      setAplicarIvaACostos(true);
      setBusquedaProd('');
      setProductosBusqueda([]);

      const toastMsg = diasFinal > 0
        ? `Compra a ${diasFinal} días proyectada en Flujo de Fondos`
        : 'Compra al contado confirmada y sincronizada';
      toast.success(toastMsg);
    } catch (err) {
      console.error(err);
      setUiMessage({ type: 'error', text: `Error al guardar: ${err?.message || 'ver consola'}` });
      toast.error('No se pudo guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-4 font-sans text-slate-700 animate-in fade-in duration-500" translate="no">
      
      {uiMessage?.text && (
        <div
          className={`mb-4 rounded-2xl border px-4 py-3 shadow-sm flex items-start justify-between gap-3 ${
            uiMessage.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : uiMessage.type === 'info'
              ? 'bg-blue-50 border-blue-200 text-blue-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          <div className="text-[11px] font-bold leading-snug">{uiMessage.text}</div>
          <button
            onClick={() => setUiMessage(null)}
            className="shrink-0 px-3 py-1 rounded-xl bg-white/70 border border-black/10 text-[10px] font-black uppercase tracking-widest"
          >
            Cerrar
          </button>
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
            <button
                onClick={() => onNavigate('compras')}
                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
                title="Volver"
            >
                <ArrowLeft size={18} />
            </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Truck size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Ingreso de Mercadería</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Gestión de abastecimiento • Stock y Costos
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                <Package size={20} />
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Renglones</p>
                <p className="text-xl font-black text-slate-800">{items.length}</p>
            </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500">
                <BadgeDollarSign size={20} />
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Operación</p>
                <p className="text-xl font-black text-slate-800">$ {currency(totalCompra)}</p>
            </div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500">
                <TrendingUp size={20} />
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Estado</p>
                <p className="text-xl font-black text-slate-800">Borrador</p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          
          {/* DATOS OPERACIÓN */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileText size={16} className="text-[#3FF2AA]" />
                    <span className="text-[10px] font-black text-white uppercase tracking-widest">Datos de Operación</span>
                </div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Proveedor */}
              <div className="relative" ref={provContainerRef}>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Proveedor
                </label>
                <div className="relative mt-1">
                  <Truck className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <input
                    data-onboarding-id="compras-proveedor-field"
                    type="text"
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                    placeholder={selectedProv ? selectedProv.razon_social : 'Buscar proveedor…'}
                    value={selectedProv ? '' : busquedaProv}
                    onFocus={() => setShowProvList(true)}
                    onChange={(e) => {
                      setBusquedaProv(e.target.value);
                      setShowProvList(true);
                    }}
                  />
                  {selectedProv && (
                    <button
                      onClick={resetProveedor}
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-white border border-slate-200 hover:bg-slate-50"
                      title="Cambiar proveedor"
                    >
                      Cambiar
                    </button>
                  )}
                </div>

                {showProvList && !selectedProv && (
                  <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden max-h-[240px] custom-scrollbar overflow-y-auto">
                    {proveedores
                      .filter((p) =>
                        (p.razon_social || '').toLowerCase().includes((busquedaProv || '').toLowerCase())
                      )
                      .slice(0, 25)
                      .map((p) => (
                        <button
                          key={p.id}
                          onClick={() => selectProveedor(p)}
                          className="w-full text-left px-4 py-3 hover:bg-slate-50 transition border-b last:border-b-0 border-slate-100"
                        >
                          <div className="text-[11px] font-black uppercase text-slate-800">{p.razon_social}</div>
                        </button>
                      ))}
                    {proveedores.length === 0 && (
                      <div className="p-4 text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">
                        Sin proveedores
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sitio destino */}
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Sitio destino
                </label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <select
                    value={selectedSitio}
                    onChange={(e) => setSelectedSitio(e.target.value)}
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                  >
                    <option value="">Seleccionar…</option>
                    {sitios.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tipo comprobante */}
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Tipo de comprobante
                </label>
                <div className="relative mt-1">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <select
                    value={tipoComprobante}
                    onChange={(e) => setTipoComprobante(e.target.value)}
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                  >
                    <option value="Factura">Factura</option>
                    <option value="Remito">Remito</option>
                    <option value="Ticket">Ticket</option>
                  </select>
                </div>
              </div>

              {/* N° comprobante */}
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Número comprobante
                </label>
                <div className="relative mt-1">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <input
                    value={numeroComprobante}
                    onChange={(e) => setNumeroComprobante(e.target.value)}
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                    placeholder="Ej: 0001-00012345"
                  />
                </div>
              </div>

              {/* Fecha y Hora */}
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha</label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <input
                    type="date"
                    value={fechaComprobante}
                    onChange={(e) => setFechaComprobante(e.target.value)}
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                  />
                </div>
              </div>
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Hora</label>
                <div className="relative mt-1">
                  <BadgeDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <input
                    type="time"
                    value={horaComprobante}
                    onChange={(e) => setHoraComprobante(e.target.value)}
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Condición de pago
                </label>
                <div className="relative mt-1">
                  <BadgeDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <select
                    value={selectedCondicion?.id || ''}
                    onChange={(e) => {
                      const cond = condicionesPago.find(c => c.id === e.target.value);
                      setSelectedCondicion(cond || null);
                      setCondicionPago(cond?.name || e.target.value);
                    }}
                    className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                  >
                    {condicionesPago.length === 0 ? (
                      <option value="">Sin condiciones configuradas</option>
                    ) : (
                      condicionesPago.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.days_until_due > 0 ? ` (${c.days_until_due} días)` : ' (Contado)'}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {selectedCondicion && selectedCondicion.days_until_due > 0 && (
                  <p className="text-[9px] font-bold text-purple-500 mt-1 ml-1 uppercase tracking-wider">
                    ⚡ Vencimiento en {selectedCondicion.days_until_due} días — se proyectará en Flujo de Fondos
                  </p>
                )}
                {selectedCondicion && selectedCondicion.days_until_due === 0 && (
                  <p className="text-[9px] font-bold text-emerald-500 mt-1 ml-1 uppercase tracking-wider">
                    ✓ Pago inmediato — se descuenta de la cuenta de origen elegida
                  </p>
                )}
              </div>

              {selectedCondicion && selectedCondicion.days_until_due === 0 && (
                <div className="md:col-span-2">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Cuenta de origen del pago
                  </label>
                  <div className="relative mt-1">
                    <BadgeDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                    <select
                      value={cuentaOrigen}
                      onChange={(e) => setCuentaOrigen(e.target.value)}
                      className="w-full h-11 pl-9 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                    >
                      <option value="">— Elegí de dónde sale la plata —</option>
                      {turnoCajaAbierto && <option value="caja">Efectivo (Caja)</option>}
                      {bancos.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  {!turnoCajaAbierto && (
                    <p className="text-[9px] font-bold text-slate-400 mt-1 ml-1 uppercase tracking-wider">
                      Sin turno de caja abierto — el efectivo no está disponible como origen
                    </p>
                  )}
                </div>
              )}

              <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Observaciones
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  rows={2}
                  className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none uppercase text-slate-700 shadow-inner resize-none"
                  placeholder="Notas operativas..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* RESUMEN CONTABLE */}
        <div className="lg:col-span-4 h-fit sticky top-4">
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center gap-2">
                <Calculator size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Resumen contable</span>
            </div>

            <div className="p-6">
                <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center text-slate-400">
                    <span className="text-[10px] font-bold uppercase">Items cargados</span>
                    <span className="text-sm font-black text-slate-900">{items.length}</span>
                </div>

                <div className="flex justify-between items-center text-slate-400">
                    <span className="text-[10px] font-bold uppercase">Unidades a stock</span>
                    <span className="text-sm font-black text-slate-900">
                    {currency(unidadesTotalesCompra)}
                    </span>
                </div>

                <div className="flex justify-between items-center text-slate-400">
                    <span className="text-[10px] font-bold uppercase">Subtotal renglones</span>
                    <span className="text-sm font-black text-slate-900">$ {currency(subtotalRenglones)}</span>
                </div>

                {/* --- IVA POR PRODUCTO (Responsable Inscripto) --- */}
                {usarIvaPorProducto && (
                  <div className="mt-2 p-4 rounded-2xl border bg-blue-50/50 border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent size={12} className="text-blue-500" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-blue-700">
                        IVA por producto (crédito fiscal)
                      </span>
                    </div>
                    {ivaBreakdown.length === 0 ? (
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-white/70 border border-slate-200 rounded-xl px-3 py-2">
                        Agregá productos para ver el IVA
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {ivaBreakdown.map((g) => (
                          <div key={g.percent} className="flex justify-between items-center bg-white/70 border border-slate-200 rounded-lg px-3 py-1.5">
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                              {g.percent === 0 ? 'Exento (0%)' : `${g.percent}% · neto $ ${currency(g.base)}`}
                            </span>
                            <span className="text-[11px] font-black text-blue-600">$ {currency(g.iva)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[8px] font-bold text-slate-400 mt-2 leading-snug">
                      La alícuota se toma de cada producto. El IVA es crédito fiscal (Libro IVA Compras) y no se carga al costo.
                    </p>
                  </div>
                )}

                {/* --- IVA TOTAL A PRORRATEAR (Modo Auto) --- */}
                {!usarIvaPorProducto && !modoTotalManual && (
                  <div className={`mt-2 p-4 rounded-2xl border transition-all ${
                    ivaActivo
                      ? 'bg-blue-50/50 border-blue-100'
                      : 'bg-slate-50/60 border-slate-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Percent size={12} className={ivaActivo ? 'text-blue-500' : 'text-slate-400'} />
                        <span className={`text-[10px] font-black uppercase tracking-widest ${
                          ivaActivo ? 'text-blue-700' : 'text-slate-400'
                        }`}>IVA de la factura</span>
                      </div>
                      {/* Toggle maestro: Activo / Inactivo. Inactivo => IVA = 0 */}
                      <button
                        onClick={() => setIvaActivo((v) => !v)}
                        className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border transition-all ${
                          ivaActivo
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-slate-400 border-slate-200'
                        }`}
                        title={ivaActivo
                          ? 'IVA activo: se calcula según el modo seleccionado'
                          : 'IVA inactivo: la factura no lleva IVA (queda en $ 0)'}
                      >
                        {ivaActivo ? '✓ Activo' : 'Inactivo'}
                      </button>
                    </div>

                    {/* Si el IVA está inactivo, mostramos solo el aviso y ocultamos los controles */}
                    {!ivaActivo ? (
                      <div className="flex items-center justify-between bg-white/70 border border-slate-200 rounded-xl px-3 py-2">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                          Sin IVA en esta factura
                        </span>
                        <span className="text-[12px] font-black text-slate-500">$ 0,00</span>
                      </div>
                    ) : (
                      <>
                        {/* Toggle: prorratear al costo o solo informativo */}
                        <div className="flex justify-end mb-2">
                          <button
                            onClick={() => setAplicarIvaACostos((v) => !v)}
                            className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border transition-all ${
                              aplicarIvaACostos
                                ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white text-slate-400 border-slate-200'
                            }`}
                            title="Si está activo, el IVA se prorratea al costo unitario de cada producto"
                          >
                            {aplicarIvaACostos ? '✓ Prorratea al costo' : 'Solo informativo'}
                          </button>
                        </div>

                        {/* Toggle %/$: define cómo se ingresa el IVA */}
                        <div className="flex gap-1 mb-2 bg-white p-1 rounded-xl border border-slate-200">
                          <button
                            onClick={() => setIvaInputModo('porcentaje')}
                            className={`flex-1 text-[9px] font-black uppercase tracking-widest py-1.5 rounded-lg transition-all ${
                              ivaInputModo === 'porcentaje'
                                ? 'bg-[#0B2A36] text-[#3FF2AA]'
                                : 'text-slate-400 hover:bg-slate-50'
                            }`}
                          >
                            % Porcentaje
                          </button>
                          <button
                            onClick={() => setIvaInputModo('monto')}
                            className={`flex-1 text-[9px] font-black uppercase tracking-widest py-1.5 rounded-lg transition-all ${
                              ivaInputModo === 'monto'
                                ? 'bg-[#0B2A36] text-[#3FF2AA]'
                                : 'text-slate-400 hover:bg-slate-50'
                            }`}
                          >
                            $ Monto exacto
                          </button>
                        </div>

                        {ivaInputModo === 'porcentaje' ? (
                          <div className="relative">
                            <DecimalInput
                              value={ivaPorcentaje}
                              onChange={(v) => setIvaPorcentaje(v)}
                              placeholder="21"
                              className="w-full pl-3 pr-9 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 font-black text-xs outline-none focus:border-blue-400"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">%</span>
                          </div>
                        ) : (
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">$</span>
                            <DecimalInput
                              value={ivaMontoManual}
                              onChange={(v) => setIvaMontoManual(v)}
                              placeholder="0.00"
                              className="w-full pl-7 pr-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 font-black text-xs outline-none focus:border-blue-400"
                            />
                          </div>
                        )}

                        {/* Display del monto calculado */}
                        <div className="flex justify-between items-center mt-2 px-1">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                            {ivaInputModo === 'porcentaje'
                              ? `${Number(ivaPorcentaje) || 0}% sobre $ ${currency(subtotalRenglones)} =`
                              : 'Monto a prorratear ='}
                          </span>
                          <span className="text-[12px] font-black text-blue-600">$ {currency(ivaMontoCalculado)}</span>
                        </div>

                        <p className="text-[8px] font-bold text-slate-400 mt-2 leading-snug">
                          {aplicarIvaACostos
                            ? 'Se distribuye proporcionalmente entre los productos según el peso de cada renglón en el subtotal. Afecta el costo unitario real.'
                            : 'Se registra solo en la cabecera de la factura. No modifica el costo unitario de los productos.'}
                        </p>
                      </>
                    )}
                  </div>
                )}

                <div className="mt-2 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Totales</span>
                    {!usarIvaPorProducto && (
                    <button
                        onClick={() => setModoTotalManual((v) => !v)}
                        className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white transition-all"
                    >
                        {modoTotalManual ? 'Modo: Manual' : 'Modo: Auto'}
                    </button>
                    )}
                    </div>

                    {(!usarIvaPorProducto && modoTotalManual) ? (
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1">Subtotal</label>
                        <DecimalInput
                            value={subtotalManual}
                            onChange={(v) => setSubtotalManual(v)}
                            className="w-full mt-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 font-black text-xs outline-none"
                        />
                        </div>
                        <div>
                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1">IVA</label>
                        <DecimalInput
                            value={ivaManual}
                            onChange={(v) => setIvaManual(v)}
                            className="w-full mt-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 font-black text-xs outline-none"
                        />
                        </div>
                        <div>
                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1">Total</label>
                        <DecimalInput
                            value={totalManual}
                            onChange={(v) => setTotalManual(v)}
                            className="w-full mt-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 font-black text-xs outline-none"
                        />
                        </div>
                    </div>
                    ) : (
                    <div className="text-[10px] font-bold uppercase text-slate-400 flex justify-between">
                        <span>Subtotal + IVA</span>
                        <span className="text-slate-700">$ {currency(subtotalCompra)} + $ {currency(ivaCompra)}</span>
                    </div>
                    )}
                </div>
                </div>

                <div className="text-center py-6 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Total a imputar</p>
                <p className="text-4xl font-black text-slate-900 tracking-tighter">$ {currency(totalCompra)}</p>
                </div>

                <div className="pt-8">
                <button
                    data-onboarding-id="compras-submit"
                    onClick={handleGuardar}
                    disabled={loading}
                    className={`w-full h-16 rounded-2xl font-black uppercase text-[11px] tracking-[0.2em] shadow-lg transition-all flex items-center justify-center gap-3
                    ${
                        showSuccessCheck
                        ? 'bg-emerald-500 text-slate-900 scale-105 shadow-emerald-500/20'
                        : 'bg-[#3FF2AA] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-500/10 disabled:opacity-30'
                    }`}
                >
                    {loading ? (
                    <>
                        <RefreshCcw size={20} className="animate-spin" /> Procesando
                    </>
                    ) : showSuccessCheck ? (
                    <>
                        <Check size={22} strokeWidth={4} className="animate-in zoom-in duration-300" /> Sincronizado
                    </>
                    ) : (
                    <>
                        <Save size={20} /> Completar compra
                    </>
                    )}
                </button>
                </div>

                <div className="mt-6 flex gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <Info size={16} className="text-[#3FF2AA] shrink-0" />
                <p className="text-[8px] font-bold text-slate-400 uppercase leading-relaxed">
                    Al confirmar: stock por unidades reales (paquetes × U./paquete), costo unitario final con IVA prorrateado, deuda proveedor, libro diario y cascada de recetas.
                </p>
                </div>
            </div>
          </div>
        </div>

        {/* RENGLONES - ANCHO COMPLETO */}
        <div className="lg:col-span-12">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[520px] overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2">
                <Box size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Renglones de factura</span>
              </div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">{items.length} ítem(s)</div>
            </div>

            <div className="p-4 flex flex-col flex-1">
              <div className="relative mb-4">
                <Barcode className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                <input
                  ref={inputProdRef}
                  data-onboarding-id="compras-product-field"
                  type="text"
                  placeholder="Buscar producto o escanear…"
                  value={busquedaProd}
                  onChange={(e) => handleProdInput(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold uppercase outline-none focus:ring-2 ring-[#3FF2AA]/20 transition-all shadow-inner"
                />

                {productosBusqueda.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-xl mt-1 shadow-2xl z-50 overflow-hidden max-h-[320px] overflow-y-auto custom-scrollbar">
                    {productosBusqueda.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => addItem(p)}
                        className="w-full text-left px-4 py-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition"
                      >
                        <div className="text-[11px] font-black uppercase text-slate-800">{p.name}</div>
                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 flex items-center gap-1.5 flex-wrap">
                          <span>{p.barcode || 'Sin barcode'} • Costo: $ {currency(p.cost_price)}</span>
                          {p.brand && (
                            <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                              {p.brand}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-auto custom-scrollbar pr-1">
                {items.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-300 text-[10px] font-black uppercase tracking-[0.2em]">
                    Sin renglones
                  </div>
                ) : (
                  itemsCalculados.map((item, idx) => (
                    <div
                      key={item.id}
                      className={`rounded-2xl border shadow-sm p-4 mb-3 ${
                        item.es_nuevo
                            ? (item.product_id ? 'bg-emerald-50/50 border-emerald-100' : 'bg-amber-50 border-amber-200')
                            : (idx % 2 === 0 ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100')
                      }`}
                    >
                      {/* --- MODO EDICIÓN (Búsqueda de producto para vincular) --- */}
                      {editingRowId === item.id ? (
                          <div className="flex flex-col gap-2 relative animate-in fade-in duration-300">
                              <div className="flex items-center gap-2 mb-1">
                                  <Search size={14} className="text-[#3FF2AA]" />
                                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Buscar producto para vincular:</span>
                              </div>
                              <div className="flex gap-2">
                                  <div className="relative flex-1">
                                      <input 
                                          ref={rowInputRef}
                                          type="text" 
                                          className="w-full h-10 px-3 bg-white border-2 border-[#3FF2AA] rounded-xl text-xs font-black uppercase outline-none shadow-sm"
                                          placeholder="Escribe el nombre del producto..."
                                          value={rowSearchTerm}
                                          onChange={(e) => handleRowSearchInput(e.target.value)}
                                      />
                                      {/* Dropdown de resultados para el renglón */}
                                      {rowSearchResults.length > 0 && (
                                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 max-h-[200px] overflow-y-auto custom-scrollbar">
                                              {rowSearchResults.map(prod => (
                                                  <button 
                                                      key={prod.id} 
                                                      onClick={() => confirmRowLink(item, prod)}
                                                      className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                                                  >
                                                      <div className="text-[10px] font-black uppercase text-slate-800">{prod.name}</div>
                                                      <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 flex-wrap">
                                                          <span>{prod.barcode || 'S/C'} • $ {currency(prod.cost_price)}</span>
                                                          {prod.brand && (
                                                              <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-wide">
                                                                  {prod.brand}
                                                              </span>
                                                          )}
                                                      </div>
                                                  </button>
                                              ))}
                                          </div>
                                      )}
                                  </div>
                                  <button 
                                    onClick={cancelEditingRow} 
                                    className="px-3 rounded-xl bg-slate-200 text-slate-500 hover:bg-slate-300 transition-colors"
                                    title="Cancelar"
                                  >
                                    <X size={16} />
                                  </button>
                              </div>
                              <div className="text-[9px] text-slate-400 mt-1 italic pl-1">
                                  Original detectado: "{item.nombre}"
                              </div>
                          </div>
                      ) : (
                          // --- MODO VISUALIZACIÓN NORMAL ---
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">

                            {/* FILA 1 (mobile): nombre + badge OCR + vincular + botón eliminar */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                    Renglón {idx + 1}
                                  </div>
                                  {item.es_nuevo && (
                                    <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">OCR</span>
                                  )}
                                </div>
                                {/* Botón eliminar: solo visible en mobile */}
                                <button
                                  onClick={() => removeItem(item.id)}
                                  className="sm:hidden text-slate-300 hover:text-rose-500 p-1.5 transition-colors"
                                  title="Quitar renglón"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>

                              <div className="mt-1 text-[12px] font-black uppercase text-slate-800 truncate">{item.nombre}</div>
                              {item.stock_tracking === 'variant' && (
                                <select
                                  value={item.variant_id || ''}
                                  onChange={(e) => updateItem(item.id, { variant_id: e.target.value || null })}
                                  className={`mt-1 w-full max-w-xs border rounded-lg px-2 py-1 text-[11px] font-bold ${item.variant_id ? 'border-slate-200 text-slate-700' : 'border-amber-300 bg-amber-50 text-amber-700'}`}
                                >
                                  <option value="">Elegí talle / color…</option>
                                  {(item.variantes || []).map((v) => (
                                    <option key={v.id} value={v.id}>{getVariantAttributesLabel(v)}{v.sku ? ` · ${v.sku}` : ''}</option>
                                  ))}
                                </select>
                              )}

                              {/* Botones de Vinculación */}
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                {item.product_id ? (
                                    <button
                                        onClick={() => startEditingRow(item)}
                                        className="text-emerald-600 flex items-center gap-1 bg-emerald-100 px-2 py-1 rounded-lg hover:bg-emerald-200 transition-colors group cursor-pointer border border-emerald-200"
                                        title="Cambiar producto vinculado"
                                    >
                                        <LinkIcon size={12} />
                                        <span className="text-[9px] font-black uppercase tracking-widest">Vinculado</span>
                                        <Replace size={12} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => startEditingRow(item)}
                                        className="text-rose-600 flex items-center gap-1 bg-rose-100 px-2 py-1 rounded-lg hover:bg-rose-200 transition-colors animate-pulse cursor-pointer border border-rose-200 shadow-sm"
                                        title="Buscar y vincular producto del sistema"
                                    >
                                        <AlertCircle size={12} />
                                        <span className="text-[9px] font-black uppercase tracking-widest">Vincular Producto</span>
                                    </button>
                                )}
                                {/* Alícuota IVA: se decide en el producto (read-only). Solo RI. */}
                                {usarIvaPorProducto && (
                                  <span
                                    className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-lg border border-blue-200 text-[9px] font-black uppercase tracking-widest"
                                    title="La alícuota se configura en el producto"
                                  >
                                    <Percent size={11} /> IVA {Number(item.iva_percentage) || 0}%
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* FILA 2 (mobile) / columna derecha (desktop): grid de campos */}
                            <div className="w-full sm:w-[560px] sm:max-w-full flex flex-col gap-2">
                              {/* Toggle Modo Paquete */}
                              <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5">
                                <div className="flex items-center gap-2">
                                  <Boxes size={12} className={item.por_paquete ? 'text-[#0B2A36]' : 'text-slate-300'} />
                                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                                    {item.por_paquete ? 'Compra por paquete' : 'Compra por unidad'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => updateItem(item.id, { por_paquete: !item.por_paquete })}
                                  className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md border transition-all ${
                                    item.por_paquete
                                      ? 'bg-[#3FF2AA] text-[#0B2A36] border-[#3FF2AA]'
                                      : 'bg-white text-slate-400 border-slate-200'
                                  }`}
                                >
                                  {item.por_paquete ? 'ON' : 'OFF'}
                                </button>
                              </div>

                              {/* Grid principal */}
                              <div className={`grid gap-2 ${item.por_paquete ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}>
                                <div>
                                  <label className="text-[8px] font-black text-slate-400 uppercase ml-1">Marca</label>
                                  <div className="w-full bg-slate-50 border border-slate-100 px-2 py-1.5 rounded-lg text-xs font-black text-slate-500 truncate min-h-[30px]">
                                    {item.marca || <span className="text-slate-300">—</span>}
                                  </div>
                                </div>

                                <div data-onboarding-id="compras-item-qty">
                                  <label className="text-[8px] font-black text-slate-400 uppercase ml-1">
                                    {item.por_paquete ? 'Cant. paquetes' : 'Cantidad'}
                                  </label>
                                  <DecimalInput
                                    value={item.cantidad}
                                    onChange={(v) => updateItem(item.id, { cantidad: v })}
                                    className="w-full bg-white border border-slate-200 px-2 py-1.5 rounded-lg text-xs font-black text-slate-800 outline-none shadow-sm focus:border-blue-400"
                                  />
                                </div>

                                {item.por_paquete && (
                                  <div>
                                    <label className="text-[8px] font-black text-slate-400 uppercase ml-1">U./paquete</label>
                                    <DecimalInput
                                      value={item.unidades_por_paquete || 1}
                                      onChange={(v) => updateItem(item.id, { unidades_por_paquete: v || 1 })}
                                      className="w-full bg-white border border-slate-200 px-2 py-1.5 rounded-lg text-xs font-black text-slate-800 outline-none shadow-sm focus:border-blue-400"
                                    />
                                  </div>
                                )}

                                <div data-onboarding-id="compras-item-cost">
                                  <label className="text-[8px] font-black text-slate-400 uppercase ml-1">
                                    {item.por_paquete ? 'Costo paquete' : 'Costo unidad'}
                                  </label>
                                  <DecimalInput
                                    value={item.costo}
                                    onChange={(v) => updateItem(item.id, { costo: v })}
                                    className="w-full bg-white border border-slate-200 px-2 py-1.5 rounded-lg text-xs font-black text-slate-800 outline-none shadow-sm focus:border-blue-400"
                                  />
                                  {/* Rótulo del último costo: el valor precargado es referencia, no el de la factura */}
                                  {item.product_id && (() => {
                                    const info = ultimosCostos[item.product_id];
                                    if (info === undefined) {
                                      return <div className="mt-1 ml-1 text-[9px] font-bold text-slate-300 leading-tight">Último costo…</div>;
                                    }
                                    if (!info) return null;
                                    const origen = describirOrigen(info);
                                    return (
                                      <div
                                        className="mt-1 ml-1 text-[9px] font-bold text-slate-400 leading-tight"
                                        title="Es el último costo que tenía el producto, no el de esta factura. Cargá el costo de la factura."
                                      >
                                        <div className="truncate">Último costo $ {currency(info.costo)}{item.por_paquete ? '/u.' : ''}</div>
                                        {origen && <div className="truncate">{origen}</div>}
                                      </div>
                                    );
                                  })()}
                                </div>

                                <div>
                                  <label className="text-[8px] font-black text-slate-400 uppercase ml-1">Subtotal</label>
                                  <div className="w-full bg-slate-50 border border-slate-100 px-2 py-1.5 rounded-lg text-xs font-black text-slate-700">
                                    $ {currency(item._subtotalNeto)}
                                  </div>
                                </div>
                              </div>

                              {item.product_id && productTracksExpiry(organization, item) && (
                                <LotesCompraEditor
                                  item={item}
                                  fechaCompraYmd={fechaComprobante}
                                  onChange={(lotes) => updateItem(item.id, { lotes })}
                                />
                              )}

                              {/* Resumen de cálculo: unidades a stock, IVA del renglón y costo unitario */}
                              {(item.por_paquete || ivaProrrateableTotal > 0 || (usarIvaPorProducto && item._ivaProrrateado > 0)) && (
                                <div className="grid grid-cols-3 gap-2 mt-1 bg-[#0B2A36]/5 border border-[#0B2A36]/10 rounded-lg p-2">
                                  <div>
                                    <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Unid. stock</div>
                                    <div className="text-[11px] font-black text-[#0B2A36]">{currency(item._unidadesStock)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                                      {usarIvaPorProducto ? 'IVA (créd. fiscal)' : 'IVA renglón'}
                                    </div>
                                    <div className="text-[11px] font-black text-blue-600">$ {currency(item._ivaProrrateado)}</div>
                                  </div>
                                  <div>
                                    <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                                      {usarIvaPorProducto ? 'Costo u. neto' : 'Costo u. final'}
                                    </div>
                                    <div className="text-[11px] font-black text-emerald-600">$ {currency(item._costoUnitarioFinal)}</div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Botón eliminar: solo visible en desktop (sm+) */}
                            <button
                              onClick={() => removeItem(item.id)}
                              className="hidden sm:block text-slate-300 hover:text-rose-500 p-1.5 mt-3 transition-colors shrink-0"
                              title="Quitar renglón"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default ComprasNueva;
